import React, { useEffect, useState } from 'react';
import { Truck, Clock, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useShippingCalculator } from '../hooks/useShippingCalculator';

interface ShippingCalculatorProps {
  frameType: string;
  frameSize: string;
  recipient: {
    firstName: string;
    lastName: string;
    addressLine1: string;
    addressLine2?: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
    phone?: string;
    company?: string;
  };
  onShippingChange: (method: 'ground' | 'overnight', cost: number, lumaMethod?: string) => void;
  selectedMethod?: 'ground' | 'overnight';
  isAddressConfirmed?: boolean;
}

const ShippingCalculator: React.FC<ShippingCalculatorProps> = ({
  frameType,
  frameSize,
  recipient,
  onShippingChange,
  selectedMethod: propSelectedMethod = 'ground',
  isAddressConfirmed = false
}) => {
  const { calculateShipping, isCalculating, shippingCosts, error } = useShippingCalculator();
  const [hasCalculated, setHasCalculated] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState<'ground' | 'overnight'>(propSelectedMethod);

  // Calculate shipping when component mounts or key props change, but only if address is confirmed
  useEffect(() => {
    const shouldCalculate = frameType && frameSize && recipient.zipCode && recipient.state && !hasCalculated && isAddressConfirmed;
    
    if (shouldCalculate) {
      console.log('Auto-calculating shipping costs...');
      calculateShipping({
        frameTypeId: frameType,
        frameSizeId: frameSize,
        recipient
      }).then(() => {
        setHasCalculated(true);
      });
    }
  }, [frameType, frameSize, recipient.zipCode, recipient.state, hasCalculated, isAddressConfirmed]);

  // Update parent component when shipping costs are available
  useEffect(() => {
    if (shippingCosts) {
      const selectedOption = shippingCosts.shippingOptions[selectedMethod];
      if (selectedOption) {
        console.log('=== SHIPPING CALCULATOR: CALLING onShippingChange ===');
        console.log('Method:', selectedMethod);
        console.log('Base cost:', selectedOption.cost);
        console.log('Available:', selectedOption.available);
        // Use base cost without any additional fees
        const finalCost = selectedOption.cost;
        console.log('Final cost:', finalCost);
        console.log('About to call onShippingChange with cost:', finalCost);
        onShippingChange(selectedMethod, finalCost, selectedOption.lumaMethod);
      }
    }
    // Note: Removed fallback costs - no default shipping cost will be set
  }, [shippingCosts, selectedMethod, onShippingChange]);

  const handleMethodChange = (method: 'ground' | 'overnight') => {
    setSelectedMethod(method);
    
    if (shippingCosts) {
      const selectedOption = shippingCosts.shippingOptions[method];
      if (selectedOption && selectedOption.available) {
        // Use base cost without any additional fees
        const finalCost = selectedOption.cost;
        console.log('=== SHIPPING CALCULATOR: METHOD CHANGE ===');
        console.log('New method:', method);
        console.log('Cost for new method:', finalCost);
        onShippingChange(method, finalCost, selectedOption.lumaMethod);
      }
    }
    // Note: No fallback costs - user must wait for calculation
  };

  const handleRecalculate = () => {
    setHasCalculated(false);
  };

  if (isCalculating) {
    return (
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 text-blue-600">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Calculating shipping costs...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <div className="flex items-center gap-2 text-red-700 mb-2">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm font-medium">Shipping calculation failed</span>
        </div>
        <div className="text-sm text-red-600 mb-3">
          Unable to calculate shipping costs. Please try again.
        </div>
        <button
          type="button"
          onClick={handleRecalculate}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 bg-white px-3 py-1 rounded border border-blue-200 hover:border-blue-300"
        >
          <RefreshCw className="w-3 h-3" />
          Try again
        </button>
      </div>
    );
  }

  if (!shippingCosts) {
    return (
      <div className="bg-gray-50 p-4 rounded-lg">
        <div className="text-sm text-gray-600 mb-3">
          Enter your complete address to calculate shipping costs
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-md opacity-50">
            <Truck className="w-4 h-4 mb-1" />
            <span className="text-sm">Standard</span>
            <span className="text-xs text-gray-400">5-10 days</span>
          </div>
          <div className="flex flex-col items-center justify-center p-3 border border-gray-200 rounded-md opacity-50">
            <Clock className="w-4 h-4 mb-1" />
            <span className="text-sm">Express</span>
            <span className="text-xs text-gray-400">3-5 days</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-lg border border-gray-200">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-gray-900">Production & Shipping</h4>
        <button
          type="button"
          onClick={handleRecalculate}
          className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          <span>Recalculate</span>
        </button>
      </div>
      
      <div className="grid grid-cols-2 gap-2 mb-3">
        <button
          type="button"
          onClick={() => handleMethodChange('ground')}
          className={`flex flex-col items-center justify-center p-3 border rounded-md transition-all ${
            selectedMethod === 'ground' 
              ? 'border-black bg-gray-50 shadow-sm' 
              : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Truck className="w-4 h-4 mb-1" />
          <span className="text-sm font-medium">Standard</span>
          <span className="text-xs text-gray-500">5-10 days</span>
          <span className="text-xs font-medium text-gray-900 mt-1">
            ${shippingCosts.shippingOptions.ground.cost.toFixed(2)}
          </span>
          {!shippingCosts.shippingOptions.ground.available && (
            <span className="text-xs text-orange-500">*Estimated</span>
          )}
        </button>
        
        <button
          type="button"
          onClick={() => handleMethodChange('overnight')}
          disabled={!shippingCosts.shippingOptions.overnight.available}
          className={`flex flex-col items-center justify-center p-3 border rounded-md transition-all ${
            !shippingCosts.shippingOptions.overnight.available
              ? 'border-gray-200 bg-gray-100 opacity-50 cursor-not-allowed'
              : selectedMethod === 'overnight' 
                ? 'border-black bg-gray-50 shadow-sm' 
                : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <Clock className="w-4 h-4 mb-1" />
          <span className="text-sm font-medium">Express</span>
          <span className="text-xs text-gray-500">3-5 days</span>
          {shippingCosts.shippingOptions.overnight.available ? (
            <span className="text-xs font-medium text-gray-900 mt-1">
              ${shippingCosts.shippingOptions.overnight.cost.toFixed(2)}
            </span>
          ) : (
            <span className="text-xs text-red-500 mt-1">Unavailable</span>
          )}
        </button>
      </div>
      
      <div className="space-y-2 text-sm border-t pt-3">
        <div className="flex justify-between items-center font-medium">
          <span className="text-gray-900">
            {selectedMethod === 'overnight' ? 'Express Order' : 'Standard Shipping'}
          </span>
          <span className="text-gray-900">
            ${selectedMethod === 'overnight' 
              ? shippingCosts.shippingOptions.overnight.cost.toFixed(2)
              : shippingCosts.shippingOptions.ground.cost.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ShippingCalculator;